import * as React from 'react'

import {
  Box,
  Button,
  CollapsableCard,
  Section,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import { AttendanceEnumsBreakViolationsHandlingEnum } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useEffect } from 'modules/core/hooks/useEffect'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { BreakSettingsType } from 'modules/attendance/hooks/useFetchBreakSettings'
import { TimeSettingsBreakConfigs } from 'modules/attendance/hooks/useFetchTimeSettingsBreakConfigurations'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { BreakSettingsFormValues } from 'modules/attendance/lib/forms/BreakSettingForm'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

import ManualBreakSettings from '../BreakSettings/ManualBreakSettings'
import DurationRuleForm from '../BreakSettings/ManualBreakSettings/DurationRules/form'
import BreakViolationHandler from './BreakViolationHandler'

interface Props {
  attendanceSetting: AttendanceSetting
  breakSetting: BreakSettingsType | undefined
  timeSettingsBreakConfigurations: TimeSettingsBreakConfigs[]
  settingForm: FormikProps<BreakSettingsFormValues>
  refetch: () => void
}

const RestAlerts = ({
  attendanceSetting,
  breakSetting,
  timeSettingsBreakConfigurations,
  settingForm,
  refetch,
}: Props) => {
  const hasPaidBreaksFeature = hasFeature('time_tracking_paid_breaks')
  const { featureAvailability: hasCorrectiveBreaksFeature } = useAttendanceFeatures(
    'time_tracking_corrective_breaks'
  )

  const attendanceSettingBreakEnabled = attendanceSetting?.hasBreaksEnabled
  const restAlertValue = settingForm.values.restTime

  const hasBreaks = timeSettingsBreakConfigurations.length
  const disableManualBreaks = hasPaidBreaksFeature && !hasBreaks
  const manualBreakEnabled = settingForm.values.manualBreaks && attendanceSettingBreakEnabled
  const isRestTimeDisabled = !manualBreakEnabled || disableManualBreaks

  const [restAlertEnabled, setRestAlertEnabled] = React.useState(
    (attendanceSettingBreakEnabled && restAlertValue) || false
  )

  const handleBreakViolationChange = (value: AttendanceEnumsBreakViolationsHandlingEnum) => {
    settingForm.setFieldValue('breakViolationsHandling', value)
  }

  const restDurationRules = breakSetting?.attendanceBreakDurationRules || []
  const [durationRuleForms, setDurationRuleForms] = React.useState<DurationRuleForm[]>(
    restDurationRules.map((rule) => new DurationRuleForm(rule))
  )

  const addDurationRule = () => setDurationRuleForms([...durationRuleForms, new DurationRuleForm()])
  const deleteDurationRule = (form: DurationRuleForm) => form.get('_destroy').set(true)
  const resetDurationRuleForms = () => {
    const validRules = durationRuleForms.filter((form) => form.get('id').value)

    setDurationRuleForms([...validRules])

    validRules.forEach((form) => form.cleanAll())
  }

  const resetDurationRuleFormsOnSave = () => {
    const validRules = durationRuleForms.filter((form) => !form.get('_destroy').value)

    setDurationRuleForms([...validRules])

    validRules.forEach((form) => form.cleanAll())
  }

  const handleRestTimeToggle = async (updatedValue: boolean) => {
    settingForm.setFieldValue('restTime', updatedValue)

    if (durationRuleForms.length === 0 && updatedValue) {
      addDurationRule()
      return
    }
    resetDurationRuleForms()

    onConfirm().then((data) => {
      if (data.success) {
        setRestAlertEnabled(updatedValue && attendanceSetting?.hasBreaksEnabled)
        refetch()
      }
    })
  }

  const onSave = async () => {
    const validRules = durationRuleForms.filter(
      (form) => !(!form.get('id').value && form.get('_destroy').value)
    )

    settingForm.setFieldValue(
      'durationRulesAttributes',
      validRules.map((rule) => rule.data())
    )

    onConfirm().then((data) => {
      if (data.success) {
        refetch()
        resetDurationRuleFormsOnSave()
      }
    })
  }

  const { running: submitting, performAction: onConfirm } = useAsyncAction(async () => {
    settingForm.handleSubmit()
    resetDurationRuleForms()
  })

  useEffect(
    () => {
      setRestAlertEnabled(attendanceSetting?.hasBreaksEnabled && !!settingForm.values.restTime)

      if (!settingForm.values.restTime || !settingForm.values.manualBreaks) {
        resetDurationRuleForms()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      attendanceSetting?.hasBreaksEnabled,
      settingForm.values.restTime,
      settingForm.values.manualBreaks,
    ]
  )

  const correctiveBreaksEnabled = hasFeature('time_tracking_corrective_breaks')

  const sectionTitle = correctiveBreaksEnabled
    ? i18n.t('time_tracking.settings.breaks.instance.rest_time.section.title_corrective')
    : i18n.t('time_tracking.settings.breaks.instance.rest_time.section.title')

  const sectionDescription = correctiveBreaksEnabled
    ? i18n.t('time_tracking.settings.breaks.instance.rest_time.section.description_corrective')
    : i18n.t('time_tracking.settings.breaks.instance.rest_time.section.description')

  const cardTitle = correctiveBreaksEnabled
    ? i18n.t('time_tracking.settings.breaks.instance.rest_time.section.title_corrective')
    : i18n.t('time_tracking.settings.breaks.instance.rest_time.title')

  const cardDescription = correctiveBreaksEnabled
    ? i18n.t('time_tracking.settings.breaks.instance.rest_time.description_corrective')
    : i18n.t('time_tracking.settings.breaks.instance.rest_time.description')

  return (
    <>
      <Section illustration="clock-question" title={sectionTitle} description={sectionDescription}>
        <CollapsableCard
          open={restAlertEnabled}
          Card={
            <Box gap="s8">
              <Box flexDirection="row" justifyContent="spaceBetween">
                <Text.Normal weight="medium">{cardTitle}</Text.Normal>
                <TogglerCheckbox
                  value={restAlertEnabled}
                  onChange={handleRestTimeToggle}
                  disabled={isRestTimeDisabled || submitting}
                  labelBefore
                  information={
                    isRestTimeDisabled
                      ? i18n.t('time_tracking.settings.breaks.instance.rest_time.tooltip')
                      : null
                  }
                />
              </Box>
              <Text.Normal color="grey800" htmlTag="div">
                {cardDescription}
              </Text.Normal>
            </Box>
          }
          Content={
            <Box gap="s16">
              <ManualBreakSettings
                settingForm={settingForm}
                durationRuleForms={durationRuleForms}
                addRule={addDurationRule}
                deleteRule={deleteDurationRule}
                onConfirm={onSave}
                hasCorrectiveBreaksFeature={hasCorrectiveBreaksFeature}
              />
              {hasCorrectiveBreaksFeature && (
                <>
                  <Box
                    border={{
                      bottom: {
                        color: 'grey400',
                        width: 's2',
                        style: 'solid',
                      },
                    }}
                  />
                  <BreakViolationHandler
                    breakViolationsHandling={
                      settingForm.values.breakViolationsHandling || 'no_action'
                    }
                    onBreakViolationChange={handleBreakViolationChange}
                  />
                  <Button onClick={onSave} loading={submitting}>
                    {i18n.t(
                      'time_tracking.settings.breaks.instance.break_violations_handling.save_changes'
                    )}
                  </Button>
                </>
              )}
            </Box>
          }
        />
      </Section>
    </>
  )
}

export default observer(RestAlerts)
